import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import ApexChart from "../components/dashboard/ApexChart";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { asArray, formatCurrency, formatDate, unwrapData } from "../lib/catalog";
import { formatBookingStatus, storedBookingStatus } from "../lib/bookingStatus";
import { BRAND } from "../lib/brand";
import { canAccessFinance, canWriteBookings, formatAgencyRole, roleFromProfile } from "../lib/agencyAccess";

function kycStatusLabel(value) {
  const status = String(value || "").trim().toLowerCase();
  if (status === "verified") return "KYC Verified";
  if (status === "rejected") return "KYC Rejected";
  return "KYC Pending";
}

function StatWidget({ icon, label, value, tone = "primary" }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card border-0 shadow-sm h-100 radius-8">
        <div className="card-body p-24">
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div>
              <span className="mb-8 fw-medium text-secondary-light text-sm">{label}</span>
              <h6 className="fw-semibold mb-0">{value}</h6>
            </div>
            <div className={`w-50-px h-50-px bg-${tone} rounded-circle d-flex justify-content-center align-items-center`}>
              <iconify-icon icon={icon} className="text-white text-2xl mb-0"></iconify-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function lastSixMonths() {
  const months = [];
  const now = new Date();
  for (let index = 5; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    months.push({
      key,
      label: date.toLocaleDateString("en-GB", { month: "short" }),
    });
  }
  return months;
}

function monthKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function groupByMonth(rows, dateKey, valueKey) {
  const totals = {};
  rows.forEach((row) => {
    const key = monthKey(row[dateKey]);
    if (!key) return;
    totals[key] = (totals[key] || 0) + (valueKey ? Number(row[valueKey] || 0) : 1);
  });
  return totals;
}

export default function DashboardPage() {
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [bookingRows, setBookingRows] = useState([]);
  const [paymentRows, setPaymentRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restrictedMessage, setRestrictedMessage] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const currentProfile = await fetchCurrentAgent();
        if (!alive) return;
        setProfile(currentProfile);

        const role = roleFromProfile(currentProfile);
        const reportRequests = [
          agentApi.get("/travel-partner/reports/dashboard"),
          agentApi.get("/travel-partner/reports/bookings", { params: { page: 1, limit: 100 } }),
        ];
        if (canAccessFinance(role)) {
          reportRequests.push(agentApi.get("/travel-partner/reports/payments", { params: { page: 1, limit: 100 } }));
        }

        try {
          const [summaryResponse, bookingsResponse, paymentsResponse] = await Promise.all(reportRequests);
          if (!alive) return;
          setDashboard(unwrapData(summaryResponse));
          setBookingRows(asArray(unwrapData(bookingsResponse)));
          setPaymentRows(asArray(unwrapData(paymentsResponse)));
          setRestrictedMessage("");
        } catch (reportError) {
          if (!alive) return;
          if (reportError?.status === 403) {
            setRestrictedMessage(
              reportError.payload?.code === "AGENCY_NOT_APPROVED"
                ? "This agency is waiting for admin approval. Live booking stats will appear after approval."
                : "Complete KYC verification to load live booking and collection stats."
            );
            return;
          }
          showApiError(reportError, "LOAD_DASHBOARD");
        }
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_DASHBOARD");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const stats = dashboard?.stats || {};
  const recentBookings = asArray(dashboard?.recentBookings).length > 0
    ? asArray(dashboard.recentBookings)
    : bookingRows.slice(0, 6);
  const notifications = asArray(dashboard?.notifications);
  const recentTransactions = asArray(dashboard?.recentTransactions);
  const months = lastSixMonths();
  const bookingTrend = groupByMonth(bookingRows, "booking_date", null);
  const paymentTrend = groupByMonth(paymentRows, "transaction_date", "amount");
  const bookingSeries = months.map((month) => Number(bookingTrend[month.key] || 0));
  const paymentSeries = months.map((month) => Number(paymentTrend[month.key] || 0));
  const monthLabels = months.map((month) => month.label);

  const bookingStatusOptions = useMemo(() => ({
    labels: ["Pending", "Confirmed", "Processing"],
    series: [
      Number(stats.pendingBookings || 0),
      Number(stats.confirmedBookings || 0),
      Number(stats.processingBookings || 0),
    ],
    colors: ["#FF9F29", "#45B369", BRAND.primary],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
        },
      },
    },
  }), [stats.pendingBookings, stats.confirmedBookings, stats.processingBookings]);

  const trendOptions = useMemo(() => ({
    series: [
      { name: "Bookings", data: bookingSeries },
    ],
    colors: [BRAND.primary],
    stroke: { curve: "smooth", width: 3 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] },
    },
    dataLabels: { enabled: false },
    xaxis: { categories: monthLabels },
    yaxis: { min: 0, forceNiceScale: true },
    legend: { show: false },
    grid: { strokeDashArray: 4 },
  }), [bookingSeries, monthLabels]);

  const collectionOptions = useMemo(() => ({
    series: [{
      name: "Amount",
      data: [
        Number(stats.successfulCollections || 0),
        Number(stats.pendingCollections || 0),
        Number(stats.totalCollections || 0),
      ],
    }],
    colors: [BRAND.primary],
    plotOptions: { bar: { borderRadius: 6, columnWidth: "42%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: ["Collected", "Pending", "Total"] },
  }), [stats.successfulCollections, stats.pendingCollections, stats.totalCollections]);

  const agentName = profile?.full_name || profile?.utp_full_name || "Travel partner";
  const agencyName = profile?.branch_name || profile?.utp_branch_name || agentName;
  const accountStatus = profile?.status || profile?.utp_status || "pending";
  const verificationStatus = profile?.verification_status || "pending";
  const agencyRole = roleFromProfile(profile);
  const agencyRoleLabel = formatAgencyRole(profile);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          title={`Welcome back, ${agentName} `}
          description={(
            <div className="mt-8">
              <p className="text-secondary small mb-0">
                Manage your Umrah bookings, pilgrims and agency operations from one place.
              </p>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-12">
                <span className="fw-semibold">{agencyName}</span>
                {profile ? (
                  <>
                    <span className="text-secondary-light" aria-hidden="true">•</span>
                    <StatusBadge value={agencyRole}>{agencyRoleLabel}</StatusBadge>
                    <span className="text-secondary-light" aria-hidden="true">•</span>
                    <StatusBadge value={accountStatus} />
                    <span className="text-secondary-light" aria-hidden="true">•</span>
                    <StatusBadge value={verificationStatus}>{kycStatusLabel(verificationStatus)}</StatusBadge>
                  </>
                ) : null}
              </div>
            </div>
          )}
          actions={(
            <div className="page-toolbar">
              {canWriteBookings(agencyRole) ? (
                <Link to="/packages" className="btn btn-primary btn-sm">New Booking</Link>
              ) : null}
              {canWriteBookings(agencyRole) ? (
                <Link to="/customers/new" className="btn btn-outline-primary btn-sm">Add Customer</Link>
              ) : null}
            </div>
          )}
        />

        {restrictedMessage ? (
          <div className="alert alert-warning" role="status">
            {restrictedMessage}
          </div>
        ) : null}

        {loading ? <PageLoader message="Loading live dashboard..." /> : null}

        {!loading ? (
          <>
            <div className="row gy-4 mb-4">
              <StatWidget icon="solar:calendar-mark-outline" label="My bookings" value={stats.totalBookings ?? 0} />
              <StatWidget icon="solar:users-group-rounded-outline" label="My pilgrims" value={stats.totalPilgrims ?? 0} tone="success" />
              <StatWidget icon="solar:wallet-money-outline" label="Collected" value={formatCurrency(stats.successfulCollections ?? 0)} tone="info" />
              <StatWidget icon="solar:clock-circle-outline" label="Outstanding" value={formatCurrency(stats.pendingCollections ?? 0)} tone="warning" />
              <StatWidget icon="solar:passport-outline" label="Visa processing" value={stats.visaProcessing ?? 0} tone="warning" />
              <StatWidget icon="solar:check-circle-outline" label="Visa issued" value={stats.visaIssued ?? 0} tone="success" />
              <StatWidget icon="solar:document-text-outline" label="Docs action" value={stats.documentActionRequired ?? 0} tone="danger" />
              <StatWidget icon="solar:bell-outline" label="Unread alerts" value={dashboard?.unreadCount ?? 0} tone="info" />
            </div>

            <div className="row gy-4 mb-4">
              <div className="col-xxl-8">
                <div className="card border-0 shadow-sm h-100 radius-8">
                  <div className="card-body p-24">
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                      <h6 className="text-lg mb-0">Bookings and collections</h6>
                      <span className="text-secondary small">Last 6 months</span>
                    </div>
                    <ApexChart type="area" height={320} options={trendOptions} />
                    <div className="small text-secondary mt-2">Collections this period: {formatCurrency(paymentSeries.reduce((sum, value) => sum + value, 0))}</div>
                  </div>
                </div>
              </div>
              <div className="col-xxl-4">
                <div className="card border-0 shadow-sm h-100 radius-8">
                  <div className="card-body p-24">
                    <h6 className="mb-3 fw-semibold text-lg">Booking mix</h6>
                    {bookingStatusOptions.series.some((value) => Number(value) > 0) ? (
                      <ApexChart type="donut" height={240} options={bookingStatusOptions} />
                    ) : (
                      <div className="text-secondary small py-5 text-center">No booking mix yet.</div>
                    )}
                    <ul className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-3 mb-0">
                      <li className="d-flex align-items-center gap-2"><span className="w-12-px h-12-px radius-2" style={{ background: "#FF9F29" }}></span><span className="text-secondary small">Pending {stats.pendingBookings ?? 0}</span></li>
                      <li className="d-flex align-items-center gap-2"><span className="w-12-px h-12-px radius-2" style={{ background: "#45B369" }}></span><span className="text-secondary small">Confirmed {stats.confirmedBookings ?? 0}</span></li>
                      <li className="d-flex align-items-center gap-2"><span className="w-12-px h-12-px radius-2" style={{ background: BRAND.primary }}></span><span className="text-secondary small">Processing {stats.processingBookings ?? 0}</span></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="row gy-4">
              <div className="col-xxl-4">
                <div className="card border-0 shadow-sm h-100 radius-8">
                  <div className="card-body p-24">
                    <h6 className="mb-3 fw-semibold text-lg">Collections</h6>
                    <ApexChart type="bar" height={260} options={collectionOptions} />
                  </div>
                </div>
              </div>
              <div className="col-xxl-5">
                <div className="card border-0 shadow-sm h-100 radius-8">
                  <div className="card-body p-24">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fw-semibold text-lg">Recent bookings</h6>
                      <Link to="/bookings" className="small text-decoration-none">View all</Link>
                    </div>
                    <Table>
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Customer</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentBookings.length > 0 ? recentBookings.slice(0, 6).map((booking) => (
                          <tr key={booking.booking_id}>
                            <td>
                              <Link to={`/bookings/${booking.booking_id}`} className="agent-table-link">
                                {booking.booking_reference || "-"}
                              </Link>
                            </td>
                            <td><PersonCell name={booking.customer_name} fallback="Unassigned" /></td>
                            <td>
                              <StatusBadge value={storedBookingStatus(booking)}>
                                {formatBookingStatus(storedBookingStatus(booking))}
                              </StatusBadge>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="3" className="text-center text-secondary py-4">No live bookings yet.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
              <div className="col-xxl-3">
                <div className="card border-0 shadow-sm h-100 radius-8">
                  <div className="card-body p-24">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fw-semibold text-lg">Alerts</h6>
                      <Link to="/notifications" className="small text-decoration-none">Inbox</Link>
                    </div>
                    {notifications.length > 0 ? notifications.slice(0, 5).map((item) => (
                      <div className="border-bottom py-2" key={item.notification_id}>
                        <div className="fw-semibold small">{item.subject || item.event_type}</div>
                        <div className="text-secondary small">{formatDate(item.created_date)}</div>
                      </div>
                    )) : recentTransactions.slice(0, 5).map((item) => (
                      <div className="border-bottom py-2" key={item.transaction_id}>
                        <div className="fw-semibold small">{formatCurrency(item.amount)}</div>
                        <div className="text-secondary small">{item.status} · {formatDate(item.transaction_date)}</div>
                      </div>
                    ))}
                    {notifications.length === 0 && recentTransactions.length === 0 ? (
                      <div className="text-secondary small">No alerts yet.</div>
                    ) : null}
                    <div className="small text-secondary mt-3">
                      {profile?.email || profile?.utp_email || "N/A"} · {profile?.verification_status || "pending"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
